import React, { useState } from 'react';
import { generateAdmissionReceipt } from '@/utils/admissionReceiptGenerator';
import { generateOnlineAdmissionReceipt } from '@/utils/onlineAdmissionReceiptGenerator';
import { generateHostelReceipt } from '@/utils/hostelReceiptGenerator';
import { generateFeePaymentReceipt } from '@/utils/feePaymentReceiptGenerator';
import { generateSalarySlip } from '@/utils/salarySlipGenerator';
import { generateBookingReceipt } from '@/utils/bookingReceiptGenerator';
import { generateFeeReceipt } from '@/utils/receiptGenerator';
import { generateInstallmentSettingPDF } from '@/utils/installmentSettingPrintGenerator';
import { generateConsultantReceipt } from '@/utils/consultantReceiptGenerator';
import { generateOtherFeeReceipt } from '@/utils/otherFeeReceiptGenerator';
import { generateAttendanceFineReceipt } from '@/utils/attendanceFineReceiptGenerator';
import { generateStudentLedgerPDF } from '@/utils/studentLedgerPrintGenerator';
import { generateLibraryReceipt } from '@/utils/libraryReceiptGenerator';
import { generateTransportReceipt } from '@/utils/transportReceiptGenerator';

const collegeInfo = {
  college_name: 'HOPE INSTITUTE',
  name: 'HOPE INSTITUTE',
  address: '123 Education Street, Kolkata',
  city: 'Kolkata',
  state: 'West Bengal',
  pincode: '700001',
  phone: '+91-9876543210',
  email: 'info@hopeinstitute.edu',
};

const TESTS = [
  {
    name: 'admissionReceipt',
    fn: () => generateAdmissionReceipt({
      receipt_no: 'ADM/2026/A001',
      student_name: 'Rajesh Kumar',
      admission_no: 'HI2026A001',
      course_name: 'B.Tech CSE',
      session: '2025-26',
      total_course_fee: 245000,
      booking_amount: 5000,
      admission_fee: 50000,
      discount_amount: 15000,
      scholarship_amount: 10000,
      balance_fee: 165000,
      payment_modes: [{ mode: 'UPI', amount: 50000, transaction_number: 'TXN123' }],
    }, collegeInfo),
  },
  {
    name: 'onlineAdmissionReceipt',
    fn: () => generateOnlineAdmissionReceipt({
      receipt_no: 'OL/2026/001',
      applicant_name: 'Priya Sharma',
      application_id: 'APP202601',
      course: 'B.Sc Nursing',
      booking_fee: 5000,
      admission_fee: 50000,
      payment_status: 'paid',
      transaction_id: 'PAY123',
      payment_date: '2026-02-14',
    }, {}, collegeInfo),
  },
  {
    name: 'hostelReceipt',
    fn: () => generateHostelReceipt({
      receipt_no: 'HOST/2026/001',
      student_name: 'Amit Roy',
      admission_no: 'HI2026B005',
      room_no: '201',
      fee_type: 'Monthly Hostel Fee',
      amount: 12500,
      paid_amount: 12500,
      payment_mode: 'Cash',
      transaction_id: 'CASH001',
      month: 'February 2026',
    }, 'HOSTEL FEE RECEIPT', collegeInfo),
  },
  {
    name: 'feePaymentReceipt',
    fn: () => generateFeePaymentReceipt({
      receipt_no: 'FEE/2026/045',
      student_name: 'Sneha Das',
      admission_number: 'HI2026A015',
      course_name: 'B.Com',
      session: '2025-26',
      total_fee: 145000,
      discount_amount: 5000,
      scholarship_amount: 0,
      previously_paid: 50000,
      amount_paid: 25000,
      total_paid_after: 75000,
      outstanding_balance: 65000,
      payment_modes: [{ mode: 'Bank Transfer', amount: 25000 }],
    }, collegeInfo),
  },
  {
    name: 'salarySlip',
    fn: () => generateSalarySlip({
      employee_name: 'Mr. Suresh Kumar',
      employee_id: 'EMP001',
      designation: 'Lecturer',
      department: 'Computer Science',
      month: 'February 2026',
      basic_salary: 45000,
      earnings: [
        { name: 'HRA', amount: 9000 },
        { name: 'DA', amount: 4500 },
      ],
      deductions: [
        { name: 'PF', amount: 5400 },
        { name: 'Professional Tax', amount: 200 },
      ],
      gross_salary: 58500,
      total_deductions: 5600,
      net_salary: 52900,
    }, collegeInfo),
  },
  {
    name: 'bookingReceipt',
    fn: () => generateBookingReceipt({
      receipt_no: 'BOOK/2026/001',
      booking_id: 'BK001',
      student_name: 'Karan Singh',
      father_name: 'Rajeev Singh',
      course_name: 'B.Tech CSE',
      total_course_fee: 245000,
      booking_amount: 5000,
      payment_mode: 'UPI',
      transaction_id: 'TXN001',
      booking_date: '2026-02-14',
    }, collegeInfo),
  },
  {
    name: 'feeReceipt',
    fn: () => generateFeeReceipt({
      receipt_no: 'FEE/2026/050',
      student_name: 'Ravi Mehta',
      admission_number: 'HI2026A055',
      course: 'B.Sc Nursing',
      semester: '3',
      fee_type: 'Tuition Fees',
      amount: 50000,
      amount_paid: 50000,
      payment_method: 'Cash',
      transaction_id: 'CASH123',
      date: '2026-02-14',
    }, collegeInfo),
  },
  {
    name: 'installmentSetting',
    fn: () => generateInstallmentSettingPDF(
      {
        student_name: 'Asha Nair',
        admission_no: 'HI2026A025',
        course_name: 'BBA',
        total_course_fee: 180000,
        seat_booking_amount: 5000,
        admission_fee: 25000,
      },
      [
        { number: 1, due_date: '2026-04-01', amount: 50000 },
        { number: 2, due_date: '2026-07-01', amount: 50000 },
        { number: 3, due_date: '2026-10-01', amount: 50000 },
      ],
      collegeInfo,
    ),
  },
  {
    name: 'consultantReceipt',
    fn: () => generateConsultantReceipt({
      receipt_no: 'CONS/2026/001',
      consultant_name: 'XYZ Consultancy',
      student_name: 'Neha Patel',
      admission_no: 'HI2026A030',
      course_name: 'B.Tech CSE',
      commission_amount: 15000,
      amount: 15000,
      payment_mode: 'Bank Transfer',
      transaction_id: 'CONS001',
      payment_date: '2026-02-14',
    }, collegeInfo),
  },
  {
    name: 'otherFeeReceipt',
    fn: () => generateOtherFeeReceipt({
      receipt_no: 'OTHER/2026/001',
      student_name: 'Mohit Verma',
      admission_no: 'HI2026A035',
      admission_number: 'HI2026A035',
      course: 'B.Com',
      course_name: 'B.Com',
      session: '2025-26',
      semester: '3',
      fee_name: 'Late Admission Fine',
      other_fee_name: 'Late Admission Fine',
      type: 'Fine',
      amount: 3000,
      paid_amount: 3000,
      late_fine: 500,
      payment_mode: 'Cash',
      payment_date: '2026-02-14',
      collected_by: 'Admin',
    }, collegeInfo),
  },
  {
    name: 'attendanceFineReceipt',
    fn: () => generateAttendanceFineReceipt({
      receipt_no: 'ATT/2026/001',
      student_name: 'Pooja Iyer',
      admission_no: 'HI2026A040',
      course_name: 'B.Sc Physics',
      fine_amount: 1500,
      amount: 1500,
      reason: 'Below 75% attendance',
      payment_mode: 'Cash',
      payment_date: '2026-02-14',
    }, collegeInfo),
  },
  {
    name: 'studentLedger',
    fn: () => generateStudentLedgerPDF({
      student: { full_name: 'Vikram Joshi', admission_number: 'HI2026A045', course_name: 'B.Tech ECE', session: '2025-26' },
      transactions: [
        { date: '2025-08-01', type: 'Fee Charged', description: 'Tuition Fee Q1', debit: 45000, credit: 0, balance: 45000 },
        { date: '2025-09-15', type: 'Payment', description: 'UPI Payment', debit: 0, credit: 30000, balance: 15000 },
        { date: '2025-12-01', type: 'Fee Charged', description: 'Tuition Fee Q2', debit: 45000, credit: 0, balance: 60000 },
        { date: '2026-01-20', type: 'Payment', description: 'Bank Transfer', debit: 0, credit: 50000, balance: 10000 },
      ],
      summary: { total_debits: 90000, total_credits: 80000, outstanding_balance: 10000 },
    }, collegeInfo),
  },
  {
    name: 'libraryReceipt',
    fn: () => generateLibraryReceipt({
      receipt_no: 'LIB/2026/001',
      student_name: 'Rohit Kapoor',
      admission_no: 'HI2026A050',
      book_title: 'Operating Systems',
      fine_amount: 50,
      damage_amount: 200,
      amount: 250,
      total_amount: 250,
      payment_mode: 'Cash',
      payment_date: '2026-02-14',
    }, 'LIBRARY FINE RECEIPT', collegeInfo),
  },
  {
    name: 'transportReceipt',
    fn: () => generateTransportReceipt({
      receipt_no: 'TRANS/2026/001',
      student_name: 'Anjali Mehta',
      admission_no: 'HI2026A055',
      route: 'Howrah Station — Campus',
      month: 'February 2026',
      fee_amount: 2500,
      amount: 2500,
      paid_amount: 2500,
      payment_mode: 'Cash',
      payment_date: '2026-02-14',
    }, 'TRANSPORT FEE RECEIPT', collegeInfo),
  },
];

export default function TestAllReceipts() {
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);

  const runOne = async (test) => {
    try {
      await test.fn();
      setResults((r) => ({ ...r, [test.name]: 'OK' }));
    } catch (e) {
      console.error(`Failed ${test.name}:`, e);
      setResults((r) => ({ ...r, [test.name]: `ERROR: ${e.message}` }));
    }
  };

  const runAll = async () => {
    setRunning(true);
    setResults({});
    for (const t of TESTS) {
      await runOne(t);
      await new Promise((r) => setTimeout(r, 800));
    }
    setRunning(false);
  };

  return (
    <div style={{ padding: 24, fontFamily: 'sans-serif' }}>
      <h1>Receipt Generator E2E Test</h1>
      <button
        onClick={runAll}
        disabled={running}
        data-testid="run-all-receipt-tests"
        style={{ padding: '12px 24px', fontSize: 16, marginBottom: 24, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
      >
        {running ? 'Running…' : 'Run All Tests'}
      </button>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ background: '#f3f4f6' }}>
            <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Generator</th>
            <th style={{ border: '1px solid #ccc', padding: 8 }}>Status</th>
            <th style={{ border: '1px solid #ccc', padding: 8 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {TESTS.map((t) => (
            <tr key={t.name}>
              <td style={{ border: '1px solid #ccc', padding: 8 }}>{t.name}</td>
              <td
                style={{
                  border: '1px solid #ccc',
                  padding: 8,
                  color: results[t.name] === 'OK' ? 'green' : (results[t.name] || '').startsWith('ERROR') ? 'red' : '#666',
                  fontWeight: 'bold',
                }}
              >
                {results[t.name] || 'pending'}
              </td>
              <td style={{ border: '1px solid #ccc', padding: 8 }}>
                <button onClick={() => runOne(t)} data-testid={`test-${t.name}`}>Generate</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
